<template>
<div id="page" class="container-fluid">
<div class="row header mx-4 my-4">
  <div class="col my-3">
    <h1>Allowable Fee Schedules
      <b-button class="ml-3" variant="outline-orange" @click="addAllowableFeeSchedule()">
      <font-awesome-icon class="orange" icon="plus"/>
      New
    </b-button></h1>
  </div>
</div>
  <div class="container-fluid">
    <div class="row data-table-header">
      <div class="col-2">Name</div>
      <div class="col-2">Effective Date</div>
      <div class="col-2">Discount Strategy</div>
      <div class="col-2">Self Pay</div>
      <div class="col-2">Medicare Multiplier</div>
      <div class="col-2"></div>
    </div>
    <div v-for="allowableFeeSchedule in allowableFeeSchedules" class="row data-table-row" :key="allowableFeeSchedule.organizationAllowableFeeScheduleId">
      <div class="column col-2">{{ allowableFeeSchedule.name }}</div>
      <div class="column col-2">{{ formatFeeScheduleDate(allowableFeeSchedule.effectiveDate) }}</div>
      <div class="column col-2">{{ discountStrategy (allowableFeeSchedule.discountStrategy) }}</div>
      <div class="column col-2">{{ selfPay(allowableFeeSchedule.isSelfPay)}}</div>
      <div class="column col-1">{{ formatPercent(allowableFeeSchedule.defaultMedicareMultiplier)}}</div>
      <div class="col-3 text-right">
        <button title="Codes" @click.prevent="goToAllowableFeeScheduleCodes(allowableFeeSchedule)" type="button" class="btn btn-outline-orange m-1 wide-button">
          <font-awesome-icon class="orange mr-2" :icon="['far', 'square-arrow-up-right']"/>Codes
        </button>
        <button title="Edit" @click.prevent="editAllowableFeeSchedule(allowableFeeSchedule)" type="button" class="btn btn-outline-orange m-1 wide-button">
          <font-awesome-icon class="orange mr-2" :icon="['far', 'edit']"/>Edit
        </button>
        <button title="Delete" @click.prevent="confirmDelete(allowableFeeSchedule.organizationAllowableFeeScheduleId)" type="button" class="btn btn-outline-orange m-1 wide-button">
          <font-awesome-icon class="orange mr-2" :icon="['far', 'trash-alt']"/>Delete
        </button>
      </div>
    </div>
  </div>
  <allowable-fee-schedules-modal :edit-allowable-fee-schedule="editingAllowableFeeSchedule" @updated="allowableFeeScheduleUpdated" :visible="showingAllowableFeeScheduleModal"
                           @update:visible="visible => showingAllowableFeeScheduleModal = visible"></allowable-fee-schedules-modal>

  <confirm-modal :title="confirmTitle"
                 @confirmed="deleteConfirmed"
                 confirmButton="Delete"
                 :copy="confirmCopy"
                 :visible="showConfirmation"
                 @update:visible="visible => showConfirmation = visible"></confirm-modal>
  <loading-modal :visible="showingLoadingModal" :return-focus="$refs.searchInput"
                 @update:visible="visible => showingLoadingModal = visible"></loading-modal>
  <paginator :totalPages="totalPages" :currentPage="currentPage" @update:currentPage="newPage => currentPage = newPage"></paginator>
</div>
</template>

<script>
  import Paginator from '@/components/Paginator'
  import ErrorsMixin from '@/mixins/ErrorsMixin'
  import moment from 'moment-timezone'
  import api from '@/services/api'
  import LoadingModal from '@/components/LoadingModal'
  import AllowableFeeSchedulesModal from '@/components/AllowableFeeSchedulesModal'
  import ConfirmModal from '@/components/ConfirmModal'

  export default {
    name: 'AllowableFeeSchedules',

    components: { LoadingModal, Paginator, AllowableFeeSchedulesModal, ConfirmModal },

    mixins: [ErrorsMixin],

    data () {
      return {
        isLoading: true,
        errors: [],
        showingAllowableFeeScheduleModal: false,
        editingAllowableFeeSchedule: {},
        totalPages: 0,
        currentPage: 1,
        pageSize: 20,
        searchTerm: '',
        confirmingDelete: true,
        showConfirmation: false,
        feeScheduleId: 0,
        showingLoadingModal: false,
        allowableFeeSchedules: []
      }
    },
    methods: {
      loadData (silent = false) {
        if (!silent) {
          this.showingLoadingModal = true
        }
        api.get('Dashboard/AllowableFeeSchedules').then(response => {
          this.allowableFeeSchedules = response.data
          this.showingLoadingModal = false
        })
      },

      addAllowableFeeSchedule () {
        this.editingAllowableFeeSchedule = {}
        this.showingAllowableFeeScheduleModal = true
      },

      goToAllowableFeeScheduleCodes (feeSchedule) {
        this.$router.push({
          name: 'allowable-fee-schedules-codes', params: { id: feeSchedule.organizationAllowableFeeScheduleId }
        })
      },

      editAllowableFeeSchedule (allowableFeeSchedule) {
        this.editingAllowableFeeSchedule = allowableFeeSchedule
        this.showingAllowableFeeScheduleModal = true
      },

      allowableFeeScheduleUpdated () {
        this.showingAllowableFeeScheduleModal = false
        this.loadData()
      },

      confirmDelete (allowableFeeSchedule) {
        this.showConfirmation = true
        this.feeScheduleId = allowableFeeSchedule
      },

      deleteFeeSchedule () {
        if (this.confirmingDelete) {
          api.delete('Dashboard/AllowableFeeSchedules/' + this.feeScheduleId).then(() => {
            this.loadData()
          }).catch(error => {
            this.isSaving = false
            if (!this.$store.canHandleNetworkError(error)) {
              this.setFromApiErrorResponse(error)
            }
          })
          this.feeScheduleId = 0
        }
      },

      deleteConfirmed (confirmation) {
        this.showConfirmation = false
        if (confirmation === true) {
          this.deleteFeeSchedule()
        }
      },
      formatFeeScheduleDate (date) {
        return moment(date).utc().format('MM/DD/YYYY')
      },
      discountStrategy (strategy) {
        if (strategy === null) {
          return '100%'
        }
        if (strategy === 'FirstFull50Remaining') {
          return '100% 50%'
        }
        if (strategy === 'Discount25EachToZero') {
          return '100% 50% 25% 0%'
        }
        if (strategy === 'Discount25Each') {
          return '100% 50% 25%'
        }
        if (strategy === 'All60') {
          return '60%'
        }
        if (strategy === 'Discount25EachWithDouble25') {
          return '100% 50% 25% 25% 0%'
        }
        if (strategy === 'FirstFullAt50ThenZero') {
          return '100% 50% 50% 0%'
        }
      },
      selfPay (pay) {
        if (pay === true) {
          return 'Yes'
        }
        if (pay === false) {
          return 'No'
        }
      },

      formatPercent (number) {
        if (number) {
          return number * 100 + '%'
        }
      }
    },

    computed: {
      confirmTitle () {
        return 'Delete Fee Schedule'
      },

      confirmButton () {
        if (this.confirmingDelete) {
          return 'Void'
        } else {
          return 'Check In'
        }

      },
      confirmCopy () {
        if (this.confirmingDelete) {
          return 'Are you sure you want to delete this fee schedule?'
        }
        return ''
      }
    },

    created () {
      this.loadData()
    }
  }

</script>
